---
id: dashboard-design
title: Dashboard Design Spec
category: monitoring
difficulty: intermediate
tags:
  - dashboard
  - grafana
  - visualization
  - metrics
  - slo
description: >
  Designs a monitoring dashboard layout with specific panels, queries,
  and thresholds for a service.
whenToUse: >
  When creating a new service dashboard or redesigning an existing one
  that has grown organically.
variables:
  - name: service_name
    desc: Name of the service
  - name: service_type
    desc: API, worker, database, etc.
  - name: available_metrics
    desc: List of metric names available
  - name: slos
    desc: Service level objectives
  - name: audience
    desc: Primary dashboard consumers
tips:
  - Follow the USE method (Utilization, Saturation, Errors) for infrastructure panels.
  - Put the most actionable panels at the top — SREs scan dashboards top-to-bottom.
  - Use consistent time ranges across related panels for easier correlation.
---

You are a monitoring visualization expert. Design a Grafana dashboard for this service.

## Service
- Name: {{service_name}}
- Type: {{service_type}}
- Key metrics available: {{available_metrics}}
- SLOs: {{slos}}

## Audience
{{audience}} (SRE, developers, management)

## Task
Design a dashboard with:
1. **Overview row** — RED metrics (Rate, Errors, Duration) at a glance
2. **SLO row** — Error budget burn, compliance status
3. **Infrastructure row** — CPU, memory, disk, network
4. **Business row** — Service-specific metrics

For each panel specify:
- Title, type (graph, stat, gauge, table, heatmap)
- PromQL/query
- Thresholds and color mapping
- Recommended time range

## Output Format (JSON)
{
  "dashboard_title": "string",
  "refresh_interval": "string",
  "rows": [
    {
      "title": "row name",
      "panels": [
        {
          "title": "panel name",
          "type": "graph | stat | gauge | table | heatmap",
          "width": 6,
          "query": "PromQL query",
          "thresholds": { "warning": 0, "critical": 0 },
          "description": "what this shows and why"
        }
      ]
    }
  ]
}

<!-- EXAMPLE_OUTPUT -->

{
  "dashboard_title": "Payment Service — Overview",
  "refresh_interval": "30s",
  "rows": [
    {
      "title": "RED Metrics",
      "panels": [
        {
          "title": "Request Rate",
          "type": "stat",
          "width": 4,
          "query": "sum(rate(http_requests_total{service=\"payment\"}[5m]))",
          "thresholds": { "warning": 100, "critical": 500 },
          "description": "Current requests per second"
        }
      ]
    }
  ]
}
